import React, { ChangeEvent, SyntheticEvent, useEffect, useRef, useState } from 'react'
import { ApolloError, useMutation, useReactiveVar } from '@apollo/client'
import { useNavigate, Link } from 'react-router-dom'
import { preLoginLocationVar } from 'apollo'
import { createPatientMutation, generateCustomerTokenMutation } from 'graphql/mutations'
import { Formatters, Validators } from 'components/forms/components/utils'
import { TextInput, FormButton, ErrorField, PasswordInput } from 'components/forms/components'
import LoadingSpinner from 'components/LoadingSpinner'
import { getRedirectable } from 'modules/auth/redirectable'
import { reportToSentry } from 'utils/reportToSentry'
import postDemographics, { DemographicsInput } from 'utils/auth/demographics'
import { MY_ACCOUNT_PATH } from 'routes'
import { useTokenStore } from 'stores/tokenStore'
import { regexErrors } from '../forms/constants'

type CreateAccountFormProps = {
  firstName: string,
  lastName: string,
  email: string,
  phone: string,
  dateOfBirth: string,
  sleepHash: string
}
// This component is for the resupply-gateway
const CreateAccountForm = ( props : CreateAccountFormProps ) => {
  const { firstName, lastName, email, phone, dateOfBirth, sleepHash } = props
  const navigate = useNavigate()
  const { setCustomerToken } = useTokenStore()
  // it appears no internal phone might return all zeros for phone number
  const missingInternalFields = {
    phone: !phone || phone === `000-000-0000`,
    email: !email
  }

  const [ loading, setLoading ] = useState( false )
  const [ errorMessage, setErrorMessage ] = useState( `` )
  const [ hasErrors, setHasErrors ] = useState( true )
  const [ showErrors, setShowErrors ] = useState<boolean>( false )
  const [ errors, setErrors ] = useState<{ [key: string]: string }>({})
  const [ formFields, setFormFields ] = useState({
    email: email ?? ``,
    phone: phone ?? ``,
    password: ``,
    passwordConfirmation: ``
  })

  useEffect( () => {
    setFormFields({
      ...formFields,
      phone: props.phone,
      email: props.email
    })
  }, [ props ] )
  const formatPhone = ( phone: string ) => {
    return phone?.replace( ` `, `-` ).replace( /[()]/g, `` )
  }

  const passwordRef = useRef( null )
  const passwordConfirmationRef = useRef( null )

  const { from } = useReactiveVar( preLoginLocationVar ) || {
    from: {
      pathname: MY_ACCOUNT_PATH,
      search: window.location.search
    }
  }

  const [ createPatient ] = useMutation( createPatientMutation, {
    errorPolicy: `all`,
    variables: {
      patientCreateInput: {
        firstname: firstName,
        lastname: lastName,
        email: formFields.email,
        date_of_birth: dateOfBirth,
        password: formFields.password,
        patient_hash: sleepHash,
        custom_attributes: [
          // when phone missing from internal, we need to send from patient input
          ...( missingInternalFields.phone ? [
            {
              attribute_code: `mobile_number`,
              value: formatPhone( formFields?.phone )
            },
            {
              attribute_code: `phone`,
              value: formatPhone( formFields?.phone )
            }
          ]: [] )
        ]
      },
      onError: ( ( error: ApolloError ) => {
        reportToSentry( new Error ( `there was a error in the create patient mutation - create account form`, {
          cause: error
        }) )
      })
    }
  })

  const [ login ] = useMutation( generateCustomerTokenMutation, {
    errorPolicy: `all`,
    fetchPolicy: `no-cache`, // Prevent caching user login creds
    variables: {
      email: formFields.email,
      password: formFields.password
    },
    onError: ( ( error ) => {
      reportToSentry( new Error ( `there was an error using the generate customer token mutation - create account form`, {
        cause: error
      }) )
    })
  })

  // Show any potential form validation errors when fields blur
  const onBlur = () => {
    setShowErrors( true )
  }

  // Perform validation of form at same time as handling changes
  const handleChange = ( event: ChangeEvent<HTMLInputElement> ) => {
    setShowErrors( false )
    setLoading( false )

    const { name, value } = event.currentTarget

    // Update form values
    setFormFields( ( prev ) => ({
      ...prev,
      [name]: value
    }) )

    // Validator map
    const validators: Record<string, () => string | null> = {
      email: () => Validators.email( value ) ? null : regexErrors.email,
      phone: () => Validators.phone( value ) ? null : regexErrors.phone,
      password: () => {
        const passwordValid = Validators.password( value )
        const passwordConfirmationValid = Validators.passwordConfirmation( value, formFields.passwordConfirmation )

        // Re-validate confirmation when password changes
        setErrors( ( prev ) => ({
          ...prev,
          passwordConfirmation: passwordConfirmationValid ? `` : regexErrors.passwordConfirmation
        }) )

        return passwordValid ? null : regexErrors.password
      },
      passwordConfirmation: () => Validators.passwordConfirmation( formFields.password, value ) ? null : regexErrors.passwordConfirmation
    }

    // Run validator if it exists
    const validate = validators[name]
    if ( validate ) {
      const error = validate()

      setErrors( ( prev ) => {
        const updatedErrors = {
          ...prev,
          [name]: error || ``
        }

        const hasAnyErrors = Object.values( updatedErrors ).some( Boolean )
        setHasErrors( hasAnyErrors )

        return updatedErrors
      })
    }
  }

  const handleSubmit = async ( event: SyntheticEvent ) => {
    event.preventDefault()
    setLoading( true )

    // when necessary internal data missing, need to update internal with patient input
    const demographicsInput: DemographicsInput = {
      ...( missingInternalFields.email && {
        emailAddress: formFields.email
      }),
      ...( missingInternalFields.phone && {
        phone: formatPhone( formFields.phone )
      })
    }

    if ( Object.values( demographicsInput ).length > 0 ) {
      const authorizationHeader = `PatientHash ${sleepHash}`
      postDemographics( authorizationHeader, demographicsInput ).catch( error => {
        reportToSentry( new Error( `create account form - failed to update demographics - email`, {
          cause: error
        }) )
      })
    }

    if ( !sleepHash ){
      reportToSentry( new Error( `create patient: createPatient mutation no sleep hash present!` ), {
        patientEmail: email,
        firstName,
        lastName,
        dob: dateOfBirth,
        formFieldEmail: formFields.email
      })
    }


    const result = await createPatient()
    if ( result?.errors ) {

      const _errorMessage = result.errors.map( errorObject => { return errorObject.message }).join( ` ` )
      reportToSentry( new Error ( `Error Creating Account (createPatient submit) ` ), {
        errorMessage: _errorMessage,
        from
      })

      setLoading( false )
      return setErrorMessage( _errorMessage )
    }

    if ( !result?.data ) {
      reportToSentry( new Error ( `Error Creating Account (createPatient response returned no data)` ) )
      setLoading( false )
      return setErrorMessage( `There was an issue creating your account.` )
    }

    const loginResult = await login()

    if ( loginResult && loginResult.data && loginResult.data.generateCustomerToken ) {
      setCustomerToken( loginResult.data.generateCustomerToken.token )
      const redirectable = getRedirectable( from )

      return navigate( redirectable?.redirectLocation ?? MY_ACCOUNT_PATH, {
        replace: true
      })
    }

    if ( loginResult?.errors ) {
      const _errorMessage = loginResult.errors.map( errorObject => { return errorObject.message }).join( ` ` )
      reportToSentry( `Error logging in newly migrated user`, {
        errorMessage: _errorMessage
      })

      setLoading( false )
      return setErrorMessage( _errorMessage )
    }
  }

  if ( loading ) {
    return (
      <div className="my-8 flex flex-col items-center justify-center mx-auto">
        <p className="mb-2">{`Creating Account ...`}</p>
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="my-5">
      <TextInput
        name="email"
        label="Email"
        type="email"
        placeholder="Email"
        value={formFields.email}
        onChange={handleChange}
        onBlur={onBlur}
        errorMessage={showErrors ? errors?.email : ``}
        required
        disabled={Boolean( email )}
      />

      {missingInternalFields.phone && (
        <TextInput
          name="phone"
          label="Phone Number"
          type="tel"
          placeholder="Phone Number"
          value={formFields.phone}
          formatter={{
            function: Formatters.phone
          }}
          autoComplete="tel"
          required
          onChange={handleChange}
          onBlur={onBlur}
          errorMessage={showErrors ? errors?.phone : ``}
        />
      )}

      <PasswordInput
        name="password"
        label="Password"
        placeholder="Password"
        value={formFields.password}
        onChange={handleChange}
        onBlur={onBlur}
        errorMessage={showErrors ? errors?.password : ``}
        required
        reference={passwordRef}
      />

      <PasswordInput
        name="passwordConfirmation"
        label="Confirm Password"
        placeholder="Confirm Password"
        value={formFields.passwordConfirmation}
        onChange={handleChange}
        onBlur={onBlur}
        errorMessage={showErrors ? errors?.passwordConfirmation : ``}
        required
        reference={passwordConfirmationRef}
      />

      <div className="flex sm:flex-col items-center mt-3">
        <FormButton disabled={hasErrors} className="sm:mr-0 btn-secondary">{`Create Account`}</FormButton>
      </div>

      {errorMessage && <ErrorField message={errorMessage} />}

      <p className="text-lg font-light text-center text-blue my-6">{`Already have an account? `}
        <Link className="a underline decoration-deepSleepBlue" to="/login">{`Click here to login`}</Link>
      </p>
    </form>
  )
}

export default CreateAccountForm
